import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { ToolTipUI } from '../../../widgets/components/UI/ToolTipUI'
import { MultilineChart } from './MultiLineChart'
import { RecordAccessRequestProps } from '../../core/_models'
import { getRecordKeeperUserListApi } from '../../core/_requests'
import { useAuth } from '../../../auth'
import { Dropdown, FormCheck, FormControl } from 'react-bootstrap'
import Skeleton from 'react-loading-skeleton'
import { toAbsoluteUrl } from '../../../../../_metronic/helpers'

const DepartmentActivity = ({
  recordAccessRequestData,
  selectedOptions,
  setSelectedOptions,
}: {
  recordAccessRequestData: RecordAccessRequestProps
  selectedOptions: any[]
  setSelectedOptions: Dispatch<SetStateAction<any[]>>
}) => {
  const { formatMessage } = useIntl()
  const [userListOptions, setUserListOptions] = useState<any[]>([])
  const [isUserListLoading, setIsUserListLoading] = useState(false)
  const { companyId } = useAuth()
  const [searchTerm, setSearchTerm] = useState('')

  const getRecordKeeperUserList = async () => {
    if (!companyId) return
    setIsUserListLoading(true)
    try {
      const {
        data: { success, data },
      } = await getRecordKeeperUserListApi(companyId)
      if (success) {
        setUserListOptions(data)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsUserListLoading(false)
    }
  }

  useEffect(() => {
    getRecordKeeperUserList()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const filteredOptions = searchTerm
    ? userListOptions.filter((option) =>
      option?.label?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    : userListOptions

  const handleCheckboxChange = (option: any) => {
    if (selectedOptions.some((item) => item.id === option.id)) {
      setSelectedOptions(selectedOptions.filter((item) => item.id !== option.id))
    } else {
      setSelectedOptions([...selectedOptions, option])
    }
  }

  return (
    <>
      <div className='bg-white rounded-2 p-4'>
        <div className='d-flex flex-md-row flex-column justify-content-between px-4 mb-2'>
          <div className='d-flex align-items-center gap-1'>
            <h4 className='m-0 font-size-14 text-muted fw-normal mb-md-0 mb-2'>
              {formatMessage({ id: 'Record Access Requests Over Time' })}
            </h4>
            <ToolTipUI
              tooltipText={formatMessage({
                id: 'Track how frequently your documents are being accessed over time. This graph will help you identify usage patterns.',
              })}
            />
          </div>

          {isUserListLoading ? (
            <Skeleton
              width={100}
              height={30}
              baseColor='var(--kt-skeleton-bg-color)'
              highlightColor='var(--kt-skeleton-highlight-bg-color)'
            />
          ) : (
            <div className='dropdown-center bg-white border rounded'>
              <Dropdown>
                <Dropdown.Toggle variant='outline-secondary' id='dropdown-basic' className='btn-sm'>
                  {selectedOptions.length === 0
                    ? `Top 10 Users`
                    : `Selected ${selectedOptions.length} of ${userListOptions.length}`}
                </Dropdown.Toggle>

                <Dropdown.Menu
                  style={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                  }}
                  className='px-3'
                >
                  {/* Search Input */}
                  <div className='px-3 py-2'>
                    <FormControl
                      type='text'
                      placeholder={`Search Users`}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  <Dropdown.Item
                    as='div'
                    className='d-flex align-items-center px-3'
                    onClick={(e) => {
                      e.stopPropagation()
                      setSelectedOptions([])
                    }}
                  >
                    <div
                      className={`d-flex justify-content-between w-100 my-3 ${selectedOptions.length === 0 ? 'selected-color p-3' : ''
                        }`}
                    >
                      <div> {`Top 10 Users`}</div>
                      {selectedOptions.length === 0 && (
                        <img
                          src={toAbsoluteUrl('/media/icons/RecordsKeeperAI/ic-tickmark.svg')}
                          alt='tick icon'
                        />
                      )}
                    </div>
                  </Dropdown.Item>

                  <div className='mx-3'>
                    <div>
                      Selected {selectedOptions.length} of {userListOptions.length}
                      {selectedOptions.length >= 10 && (
                        <span className='text-danger ms-2'>(Maximum 10 selections)</span>
                      )}
                    </div>
                    <hr className='mt-2' />
                  </div>

                  {/* User List */}
                  {filteredOptions.length > 0 ? (
                    filteredOptions.map((option) => {
                      const isSelected = selectedOptions.some((item) => item.id === option.id)
                      const isDisabled = selectedOptions.length >= 10 && !isSelected

                      return (
                        <Dropdown.Item
                          key={option.id}
                          as='div'
                          className={`d-flex align-items-center px-3 ${isDisabled ? 'opacity-50' : ''
                            }`}
                          onClick={(e) => e.stopPropagation()} // Prevent dropdown from closing
                        >
                          <FormCheck
                            type='checkbox'
                            checked={isSelected}
                            onChange={() => {
                              if (!isSelected && selectedOptions.length >= 10) {
                                return
                              }
                              handleCheckboxChange(option)
                            }}
                            id={`checkbox-${option.id}`} // Add a unique ID
                            className='text-dark'
                            disabled={isDisabled}
                          >
                            <FormCheck.Input
                              type='checkbox'
                              checked={isSelected}
                              disabled={isDisabled}
                            />
                            <FormCheck.Label htmlFor={`checkbox-${option.id}`}>
                              <div className='d-flex flex-column'>
                                <div>{option.label.split(',')[0]}</div>
                                <div className='text-muted small'>{option.label.split(',')[1]}</div>
                              </div>
                            </FormCheck.Label>
                          </FormCheck>
                        </Dropdown.Item>
                      )
                    })
                  ) : (
                    <div className='px-3 py-2'>{formatMessage({ id: 'No users found' })}</div>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          )}
        </div>
        <MultilineChart recordAccessRequestData={recordAccessRequestData} />
      </div>
    </>
  )
}

export default DepartmentActivity
